import { useEffect, useState } from 'react'
import { AlertTriangle, CreditCard, HeartPulse, Phone, ShieldAlert, UserCheck, UserPlus, X, createLucideIcon } from 'lucide-react'
import Modal from '../../../components/ERP/Modal/Modal'
import ErpPeriodDropdown from '../../../components/ERP/ErpPeriodDropdown/ErpPeriodDropdown'
import { CalendarDatePicker } from '../../../components/ERP/Calendar/Calendar'
import { patientService } from '../../../services/patientApi'
import { CLINICAL_ALERT_LIMIT, todayInput } from './patientFormat'
import './PatientsPage.css'

function parseValidUntil(value) {
  const match = String(value || '').trim().match(/^(\d{2})\/(\d{4})$/)
  if (!match) return null
  return `${match[2]}-${match[1]}-01`
}

const STATUS_OPTIONS = [
  { value: 'internado', label: 'Internado' },
  { value: 'estavel', label: 'Estável' },
  { value: 'observacao', label: 'Em observação' },
  { value: 'ambulatorial', label: 'Ambulatorial' },
  { value: 'alta', label: 'Alta concedida' }
]

const GENDER_OPTIONS = ['Feminino', 'Masculino', 'Outro'].map((item) => ({ value: item, label: item }))
const BLOOD_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((item) => ({ value: item, label: item }))
const ROOM_OPTIONS = [
  'Apartamento Privativo',
  'Enfermaria',
  'UTI Adulto',
  'UTI Neonatal',
  'Ambulatório Geral'
].map((item) => ({ value: item, label: item }))

const Pen = createLucideIcon('pen', [
  ['path', { d: 'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z' }],
  ['path', { d: 'm15 5 4 4' }]
])

function genderOption(value) {
  return { feminino: 'Feminino', masculino: 'Masculino', outro: 'Outro' }[value] || 'Feminino'
}

function validLabel(value) {
  const match = String(value || '').slice(0, 10).match(/^(\d{4})-(\d{2})/)
  return match ? `${match[2]}/${match[1]}` : ''
}

function withCurrent(options, value) {
  if (!value || options.some((item) => item.value === value)) return options
  return [{ value, label: value }, ...options]
}

function FieldDropdown({ value, options, onChange, searchable = false, ariaLabel }) {
  return (
    <ErpPeriodDropdown
      className="patients-field-dropdown"
      label=""
      align="left"
      portal
      searchable={searchable}
      ariaLabel={ariaLabel}
      value={value}
      options={options}
      onChange={onChange}
    />
  )
}

function NewPatientModal({ isOpen, onClose, onCreated, patient = null }) {
  const [name, setName] = useState('')
  const [cpf, setCpf] = useState('')
  const [rg, setRg] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [gender, setGender] = useState('Feminino')
  const [bloodType, setBloodType] = useState('O+')
  const [photo, setPhoto] = useState(null)
  const [status, setStatus] = useState('internado')
  const [admissionDate, setAdmissionDate] = useState(todayInput)
  const [bed, setBed] = useState('')
  const [roomCategory, setRoomCategory] = useState('Apartamento Privativo')
  const [attendingUserId, setAttendingUserId] = useState('')
  const [staff, setStaff] = useState([])
  const [allergyInput, setAllergyInput] = useState('')
  const [allergies, setAllergies] = useState([])
  const [alertInput, setAlertInput] = useState('')
  const [clinicalAlerts, setClinicalAlerts] = useState([])
  const [insuranceProvider, setInsuranceProvider] = useState('')
  const [insurancePlan, setInsurancePlan] = useState('')
  const [insuranceCard, setInsuranceCard] = useState('')
  const [insuranceValid, setInsuranceValid] = useState('')
  const [customerQuery, setCustomerQuery] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [customers, setCustomers] = useState([])
  const [phone, setPhone] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [email, setEmail] = useState('')
  const [street, setStreet] = useState('')
  const [number, setNumber] = useState('')
  const [neighborhood, setNeighborhood] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [emergencyName, setEmergencyName] = useState('')
  const [emergencyRelationship, setEmergencyRelationship] = useState('')
  const [emergencyPhone, setEmergencyPhone] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isOpen) return
    patientService.staff().then((response) => setStaff(response.data || [])).catch(() => {})
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || patient) return
    setAdmissionDate(todayInput())
  }, [isOpen, patient])

  useEffect(() => {
    if (!isOpen || !patient) return
    setName(patient.name || '')
    setCpf(patient.cpf || '')
    setRg(patient.rg || '')
    setBirthDate(String(patient.birth_date || '').slice(0, 10))
    setGender(genderOption(patient.gender))
    setBloodType(patient.blood_type || 'O+')
    setPhoto(null)
    setStatus(STATUS_OPTIONS.some((item) => item.value === patient.status) ? patient.status : 'internado')
    setAdmissionDate(String(patient.admission_date || '').slice(0, 10) || todayInput())
    setBed(patient.bed || '')
    setRoomCategory(patient.room_category || 'Apartamento Privativo')
    setAttendingUserId(patient.attending_user_id ? String(patient.attending_user_id) : '')
    setAllergyInput('')
    setAllergies((patient.allergies || []).map((item) => item.description).filter(Boolean))
    setAlertInput('')
    setClinicalAlerts((patient.alerts || []).map((item) => item.description).filter(Boolean))
    setInsuranceProvider(patient.insurance_provider || '')
    setInsurancePlan(patient.insurance_plan || '')
    setInsuranceCard(patient.insurance_card || '')
    setInsuranceValid(validLabel(patient.insurance_valid_until))
    setError('')
  }, [isOpen, patient])

  const searchCustomers = async (value) => {
    setCustomerQuery(value)
    setCustomerId('')
    if (value.trim().length < 2) {
      setCustomers([])
      return
    }
    const response = await patientService.customers(value.trim())
    setCustomers(response.data || [])
  }

  const addAllergy = () => {
    if (!allergyInput.trim()) return
    setAllergies((list) => [...list, allergyInput.trim()])
    setAllergyInput('')
  }

  const addAlert = () => {
    const text = alertInput.trim().slice(0, CLINICAL_ALERT_LIMIT)
    if (!text) return
    setClinicalAlerts((list) => [...list, text])
    setAlertInput('')
  }

  const submit = async (event) => {
    event.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    setError('')
    try {
      const genderApi = { Feminino: 'feminino', Masculino: 'masculino', Outro: 'outro' }[gender]
      const address = [street, number, neighborhood, city && state ? `${city}/${state}` : city || state, zipCode]
        .filter(Boolean)
        .join(', ')
      const payload = {
        name: name.trim(),
        cpf,
        rg,
        birthDate: birthDate || null,
        gender: genderApi,
        bloodType,
        status,
        admissionDate: admissionDate || todayInput(),
        bed,
        roomCategory,
        attendingUserId: attendingUserId || null,
        insuranceProvider,
        insurancePlan,
        insuranceCard,
        insuranceValidUntil: parseValidUntil(insuranceValid),
        allergies,
        alerts: clinicalAlerts
      }
      const saved = patient
        ? await patientService.update(patient.id, payload)
        : await patientService.create({
          ...payload,
          phone,
          whatsapp,
          email,
          address,
          emergencyName,
          emergencyRelationship,
          emergencyPhone,
          customerId: customerId || null
        })
      if (photo) await patientService.addPhoto(saved.data.id, photo)
      onCreated(saved.data)
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || (patient ? 'Não foi possível salvar o prontuário' : 'Não foi possível admitir o paciente'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={patient ? 'Editar prontuário' : 'Admissão de paciente'}
      subtitle="Prontuário eletrônico"
      icon={patient ? <Pen size={22} /> : <UserPlus size={22} />}
      size="large"
      footer={(
        <>
          <button type="button" className="btn-cancel" onClick={onClose}>Cancelar</button>
          <button type="submit" form="new-patient-form" className="btn-primary" disabled={saving}>{saving ? 'Salvando...' : (patient ? 'Salvar prontuário' : 'Admitir paciente')}</button>
        </>
      )}
    >
      <form id="new-patient-form" onSubmit={submit}>
        {error && <p className="patients-alert-strip">{error}</p>}
        <section className="patients-form-section">
          <h3><UserCheck size={16} /> 1. Identificação pessoal</h3>
          <div className="patients-form-grid patients-form-grid--3">
            <div className="patients-span-2">
              <label>Nome completo *</label>
              <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Maria Clara dos Santos" />
            </div>
            <div className="patients-evolution-date">
              <label>Data de nascimento</label>
              <CalendarDatePicker
                value={birthDate}
                onChange={setBirthDate}
                maxDate={todayInput()}
                title="Data de nascimento"
                placeholder="Selecionar data"
              />
            </div>
            <div>
              <label>CPF</label>
              <input value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="000.000.000-00" />
            </div>
            <div>
              <label>RG</label>
              <input value={rg} onChange={(e) => setRg(e.target.value)} placeholder="00.000.000-0" />
            </div>
            <div className="patients-pair">
              <div>
                <label>Gênero</label>
                <FieldDropdown value={gender} options={GENDER_OPTIONS} onChange={setGender} />
              </div>
              <div>
                <label>Tipo sang.</label>
                <FieldDropdown value={bloodType} options={withCurrent(BLOOD_OPTIONS, bloodType)} onChange={setBloodType} />
              </div>
            </div>
            <div className="patients-span-3">
              <label>{patient ? 'Trocar foto' : 'Foto do paciente'}</label>
              <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
            </div>
          </div>
        </section>

        <section className="patients-form-section">
          <h3><HeartPulse size={16} /> 2. Regime de internação</h3>
          <div className="patients-form-grid patients-form-grid--4">
            <div>
              <label>Status atual *</label>
              <FieldDropdown value={status} options={STATUS_OPTIONS} onChange={setStatus} />
            </div>
            <div className="patients-evolution-date">
              <label>Data da internação</label>
              <CalendarDatePicker
                value={admissionDate}
                onChange={setAdmissionDate}
                title="Data da internação"
                placeholder="Selecionar data"
              />
            </div>
            <div>
              <label>Acomodação</label>
              <FieldDropdown value={roomCategory} options={withCurrent(ROOM_OPTIONS, roomCategory)} onChange={setRoomCategory} />
            </div>
            <div>
              <label>Leito / quarto</label>
              <input value={bed} onChange={(e) => setBed(e.target.value)} placeholder="Ex: Leito 204 - Ala B" />
            </div>
            <div>
              <label>Médico responsável</label>
              <FieldDropdown
                searchable
                ariaLabel="Médico responsável"
                value={attendingUserId}
                options={[
                  { value: '', label: 'Selecione um usuário' },
                  ...staff.map((user) => ({ value: String(user.id), label: user.name }))
                ]}
                onChange={(value) => setAttendingUserId(value ? String(value) : '')}
              />
            </div>
          </div>
          <div className="patients-form-grid patients-form-grid--2" style={{ marginTop: '0.85rem' }}>
            <div className="patients-box patients-box--rose">
              <label className="patients-inline"><AlertTriangle size={14} /> Alergias conhecidas</label>
              <div className="patients-add-row">
                <input value={allergyInput} onChange={(e) => setAllergyInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAllergy() } }} placeholder="Ex: Penicilina, Látex..." />
                <button type="button" className="patients-mini patients-mini--rose" onClick={addAllergy}>Add</button>
              </div>
              <div className="patients-tags">
                {allergies.map((item, index) => (
                  <span key={`${item}-${index}`} className="patients-tag patients-tag--rose">
                    {item}
                    <button type="button" onClick={() => setAllergies((list) => list.filter((_, i) => i !== index))} aria-label="Remover"><X size={12} /></button>
                  </span>
                ))}
              </div>
            </div>
            <div className="patients-box patients-box--amber">
              <label className="patients-inline"><ShieldAlert size={14} /> Alertas clínicos</label>
              <div className="patients-add-row">
                <input value={alertInput} maxLength={CLINICAL_ALERT_LIMIT} onChange={(e) => setAlertInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAlert() } }} placeholder="Ex: Risco de queda..." />
                <button type="button" className="patients-mini patients-mini--amber" onClick={addAlert}>Add</button>
              </div>
              <div className="patients-tags">
                {clinicalAlerts.map((item, index) => (
                  <span key={`${item}-${index}`} className="patients-tag patients-tag--amber">
                    {item}
                    <button type="button" onClick={() => setClinicalAlerts((list) => list.filter((_, i) => i !== index))} aria-label="Remover"><X size={12} /></button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="patients-form-section">
          <h3><CreditCard size={16} /> 3. Convênio e faturamento</h3>
          <div className="patients-form-grid patients-form-grid--4">
            <div><label>Operadora</label><input value={insuranceProvider} onChange={(e) => setInsuranceProvider(e.target.value)} placeholder="Ex: Unimed" /></div>
            <div><label>Plano</label><input value={insurancePlan} onChange={(e) => setInsurancePlan(e.target.value)} placeholder="Ex: Top Nacional" /></div>
            <div><label>Carteirinha</label><input value={insuranceCard} onChange={(e) => setInsuranceCard(e.target.value)} /></div>
            <div><label>Validade</label><input value={insuranceValid} onChange={(e) => setInsuranceValid(e.target.value)} placeholder="MM/AAAA" /></div>
            {!patient && <div className="patients-span-4">
              <label>Cliente pagador no ERP</label>
              <input value={customerQuery} onChange={(e) => searchCustomers(e.target.value)} placeholder="Buscar cliente já cadastrado" />
              <p className="patients-muted">Se ficar vazio, o sistema cria um cliente com os dados deste paciente.</p>
              {customers.length > 0 && (
                <div className="patients-picker">
                  {customers.map((customer) => (
                    <button type="button" key={customer.id} onClick={() => { setCustomerId(customer.id); setCustomerQuery(customer.name); setCustomers([]) }}>{customer.name}</button>
                  ))}
                </div>
              )}
            </div>}
          </div>
        </section>

        {!patient && <section className="patients-form-section">
          <h3><Phone size={16} /> 4. Contatos e familiar</h3>
          <div className="patients-form-grid patients-form-grid--3">
            <div><label>Telefone</label><input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(11) 98765-4321" /></div>
            <div><label>WhatsApp</label><input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="5511987654321" /></div>
            <div><label>E-mail</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            <div><label>Rua</label><input value={street} onChange={(e) => setStreet(e.target.value)} /></div>
            <div><label>Número</label><input value={number} onChange={(e) => setNumber(e.target.value)} /></div>
            <div><label>Bairro</label><input value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} /></div>
            <div><label>Cidade</label><input value={city} onChange={(e) => setCity(e.target.value)} /></div>
            <div><label>UF</label><input value={state} onChange={(e) => setState(e.target.value)} /></div>
            <div><label>CEP</label><input value={zipCode} onChange={(e) => setZipCode(e.target.value)} /></div>
            <div><label>Emergência</label><input value={emergencyName} onChange={(e) => setEmergencyName(e.target.value)} /></div>
            <div><label>Parentesco</label><input value={emergencyRelationship} onChange={(e) => setEmergencyRelationship(e.target.value)} /></div>
            <div><label>Telefone da emergência</label><input value={emergencyPhone} onChange={(e) => setEmergencyPhone(e.target.value)} /></div>
          </div>
        </section>}
      </form>
    </Modal>
  )
}

export default NewPatientModal
